/**
 * @fileoverview HypayPaymentForm - טופס תשלום כספית (Hypay)
 * @description קומפוננטה מקיפה לעיבוד תשלומים דרך כספית עם תמיכה מלאה ב-API שלהם
 * 
 * @features
 * - יצירת סשן תשלום עם כספית
 * - הפניה לעמוד התשלום המאובטח של כספית
 * - טיפול בקריאות חוזרות והצגת תוצאות
 * - תמיכה בכרטיסי אשראי ישראליים
 * - Apple Pay & Google Pay (דרך כספית)
 * - חשבוניות אוטומטיות עם EzCount
 * - תמיכה בתשלומים
 */
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  CreditCard, 
  User, 
  Phone, 
  Shield, 
  Lock,
  CheckCircle,
  AlertCircle,
  Loader2,
  ExternalLink,
  Receipt,
  Smartphone
} from "lucide-react";
import { useUserStore } from "../stores/useUserStore";
import { useCartStore } from "../stores/useCartStore";

const HypayPaymentForm = ({ 
  totalAmount, 
  onPaymentSuccess, 
  onPaymentError,
  items = []
}) => {
  const { user } = useUserStore();
  const { clearCart } = useCartStore();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  
  // פרטי לקוח
  const [customerDetails, setCustomerDetails] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    idNumber: user?.userId || "",
    email: user?.email || "",
    phone: user?.phone || "",
    mobile: user?.mobile || "",
    street: user?.address?.street || "",
    city: user?.address?.city || "",
    zip: user?.address?.zip || ""
  });
  
  // הגדרות תשלום
  const [paymentSettings, setPaymentSettings] = useState({
    installments: 1,
    language: "HEB",
    template: 1,
    sendEmail: true,
    generateInvoice: true
  });

  /**
   * 🚀 יצירת סשן תשלום עם כספית
   */
  const createHypaySession = async () => {
    setIsProcessing(true);
    setValidationErrors({});

    try {
      // ולידציה של פרטי לקוח
      const errors = validateCustomerDetails();
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setIsProcessing(false);
        return;
      }

      const response = await fetch('/api/hypay/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          items: items.map(item => ({
            _id: item._id,
            name: item.name,
            price: item.price,
            quantity: item.quantity
          })),
          customerDetails,
          paymentMethod: "credit_card",
          installments: paymentSettings.installments,
          language: paymentSettings.language,
          template: paymentSettings.template
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'שגיאה ביצירת סשן תשלום');
      }

      const data = await response.json();
      
      setPaymentUrl(data.paymentUrl);
      setSessionId(data.sessionId);
      setCurrentStep(2);

    } catch (error) {
      console.error('Error creating Hypay session:', error);
      setValidationErrors({ general: error.message });
      onPaymentError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * 🔍 ולידציה של פרטי לקוח
   */
  const validateCustomerDetails = () => {
    const errors = {};

    if (!customerDetails.firstName?.trim()) {
      errors.firstName = "שם פרטי חובה";
    }

    if (!customerDetails.lastName?.trim()) {
      errors.lastName = "שם משפחה חובה";
    }

    if (!customerDetails.idNumber?.trim()) {
      errors.idNumber = "מספר זהות חובה";
    } else if (!/^\d{9}$/.test(customerDetails.idNumber.replace(/\D/g, ''))) {
      errors.idNumber = "מספר זהות לא תקין";
    }

    if (!customerDetails.email?.trim()) {
      errors.email = "כתובת אימייל חובה";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerDetails.email)) {
      errors.email = "כתובת אימייל לא תקינה";
    }

    if (!customerDetails.phone?.trim() && !customerDetails.mobile?.trim()) {
      errors.phone = "נדרש מספר טלפון או נייד";
    }

    return errors;
  };

  /**
   * 📱 הפניה לעמוד התשלום של כספית
   */
  const redirectToHypayPayment = () => {
    if (paymentUrl) {
      // פתיחה בחלון חדש כדי לשמור על המצב
      window.open(paymentUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
      setCurrentStep(3);
    }
  };

  /**
   * 📊 בדיקת סטטוס תשלום
   */
  const checkPaymentStatus = async () => {
    if (!sessionId) return;

    try {
      const response = await fetch(`/hypay/status/${sessionId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.status === 'paid') {
          clearCart();
          onPaymentSuccess?.(data);
          setCurrentStep(4);
        } else if (data.status === 'failed') {
          onPaymentError?.(new Error('התשלום נכשל'));
          setCurrentStep(1);
        }
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  /**
   * 🔄 בדיקה תקופתית של סטטוס התשלום
   */
  useEffect(() => {
    if (currentStep === 3 && sessionId) {
      const interval = setInterval(checkPaymentStatus, 3000);
      return () => clearInterval(interval);
    }
  }, [currentStep, sessionId]);

  /**
   * 🎨 עיצוב שלב 1: פרטי לקוח
   */
  const renderCustomerDetailsStep = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 rtl:space-x-reverse">
          <Shield className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-blue-900">
            תשלום מאובטח דרך כספית
          </h3>
        </div>
        <p className="text-blue-700 text-sm mt-2">
          התשלום יתבצע באתר המאובטח של כספית עם הצפנה מלאה
        </p>
      </div>

      {validationErrors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700">{validationErrors.general}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* שם פרטי */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4 inline ml-1" />
            שם פרטי *
          </label>
          <input
            type="text"
            value={customerDetails.firstName}
            onChange={(e) => setCustomerDetails(prev => ({
              ...prev,
              firstName: e.target.value
            }))}
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              validationErrors.firstName ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="הכנס שם פרטי"
          />
          {validationErrors.firstName && (
            <span className="text-red-500 text-sm">{validationErrors.firstName}</span>
          )}
        </div>

        {/* שם משפחה */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            שם משפחה *
          </label>
          <input
            type="text"
            value={customerDetails.lastName}
            onChange={(e) => setCustomerDetails(prev => ({
              ...prev,
              lastName: e.target.value
            }))}
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              validationErrors.lastName ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="הכנס שם משפחה"
          />
          {validationErrors.lastName && (
            <span className="text-red-500 text-sm">{validationErrors.lastName}</span>
          )}
        </div>

        {/* מספר זהות */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            מספר זהות *
          </label>
          <input
            type="text"
            value={customerDetails.idNumber}
            onChange={(e) => setCustomerDetails(prev => ({
              ...prev,
              idNumber: e.target.value.replace(/\D/g, '').slice(0, 9)
            }))}
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              validationErrors.idNumber ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="000000000"
            maxLength="9"
          />
          {validationErrors.idNumber && (
            <span className="text-red-500 text-sm">{validationErrors.idNumber}</span>
          )}
        </div>

        {/* אימייל */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            כתובת אימייל *
          </label>
          <input
            type="email"
            value={customerDetails.email}
            onChange={(e) => setCustomerDetails(prev => ({
              ...prev,
              email: e.target.value
            }))}
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              validationErrors.email ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="example@email.com"
          />
          {validationErrors.email && (
            <span className="text-red-500 text-sm">{validationErrors.email}</span>
          )}
        </div>

        {/* טלפון */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Phone className="w-4 h-4 inline ml-1" />
            טלפון קווי
          </label>
          <input
            type="tel"
            value={customerDetails.phone}
            onChange={(e) => setCustomerDetails(prev => ({
              ...prev,
              phone: e.target.value
            }))}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="03-1234567"
          />
        </div>

        {/* נייד */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Smartphone className="w-4 h-4 inline ml-1" />
            טלפון נייד *
          </label>
          <input
            type="tel"
            value={customerDetails.mobile}
            onChange={(e) => setCustomerDetails(prev => ({
              ...prev,
              mobile: e.target.value
            }))}
            className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              validationErrors.phone ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="050-1234567"
          />
          {validationErrors.phone && (
            <span className="text-red-500 text-sm">{validationErrors.phone}</span>
          )}
        </div>
      </div>

      {/* כתובת */}
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-gray-900">כתובת (אופציונלי)</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              רחוב ומספר בית
            </label>
            <input
              type="text"
              value={customerDetails.street}
              onChange={(e) => setCustomerDetails(prev => ({
                ...prev,
                street: e.target.value
              }))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="רחוב הרצל 123"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              מיקוד
            </label>
            <input
              type="text"
              value={customerDetails.zip}
              onChange={(e) => setCustomerDetails(prev => ({
                ...prev,
                zip: e.target.value
              }))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="12345"
            />
          </div>

          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              עיר
            </label>
            <input
              type="text"
              value={customerDetails.city}
              onChange={(e) => setCustomerDetails(prev => ({
                ...prev,
                city: e.target.value
              }))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="תל אביב"
            />
          </div>
        </div>
      </div>

      {/* הגדרות תשלום */}
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-gray-900">הגדרות תשלום</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              מספר תשלומים
            </label>
            <select
              value={paymentSettings.installments}
              onChange={(e) => setPaymentSettings(prev => ({
                ...prev,
                installments: parseInt(e.target.value)
              }))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={1}>תשלום אחד</option>
              <option value={2}>2 תשלומים</option>
              <option value={3}>3 תשלומים</option>
              <option value={4}>4 תשלומים</option>
              <option value={5}>5 תשלומים</option>
              <option value={6}>6 תשלומים</option>
              <option value={12}>12 תשלומים</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              שפת עמוד התשלום
            </label>
            <select
              value={paymentSettings.language}
              onChange={(e) => setPaymentSettings(prev => ({
                ...prev,
                language: e.target.value
              }))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="HEB">עברית</option>
              <option value="ENG">English</option>
            </select>
          </div>
        </div>

        <div className="flex items-center space-x-2 rtl:space-x-reverse">
          <input
            type="checkbox"
            id="sendEmail"
            checked={paymentSettings.sendEmail}
            onChange={(e) => setPaymentSettings(prev => ({
              ...prev,
              sendEmail: e.target.checked
            }))}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          />
          <label htmlFor="sendEmail" className="text-sm text-gray-700">
            שלח אישור תשלום באימייל
          </label>
        </div>

        <div className="flex items-center space-x-2 rtl:space-x-reverse">
          <input
            type="checkbox"
            id="generateInvoice"
            checked={paymentSettings.generateInvoice}
            onChange={(e) => setPaymentSettings(prev => ({
              ...prev,
              generateInvoice: e.target.checked
            }))}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          />
          <label htmlFor="generateInvoice" className="text-sm text-gray-700">
            יצירת חשבונית אוטומטית
          </label>
        </div>
      </div>

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={createHypaySession}
        disabled={isProcessing}
        className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-semibold text-lg
                 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                 disabled:opacity-50 disabled:cursor-not-allowed
                 flex items-center justify-center space-x-2 rtl:space-x-reverse"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>יוצר סשן תשלום...</span>
          </>
        ) : (
          <>
            <Lock className="w-5 h-5" />
            <span>המשך לתשלום מאובטח</span>
          </>
        )}
      </motion.button>
    </motion.div>
  );

  /**
   * 🎨 עיצוב שלב 2: הפניה לתשלום
   */
  const renderPaymentRedirectStep = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="text-center space-y-6"
    >
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-green-900 mb-2">
          סשן התשלום נוצר בהצלחה!
        </h3>
        <p className="text-green-700">
          כעת תועבר לעמוד התשלום המאובטח של כספית
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-center space-x-2 rtl:space-x-reverse mb-3">
          <Shield className="w-5 h-5 text-blue-600" />
          <span className="text-blue-900 font-medium">מידע חשוב</span>
        </div>
        <ul className="text-blue-700 text-sm space-y-1">
          <li>• התשלום מתבצע באתר המאובטח של כספית</li>
          <li>• אל תסגור את החלון עד לסיום התשלום</li>
          <li>• לאחר התשלום תחזור אוטומטית לאתר</li>
        </ul>
      </div>

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={redirectToHypayPayment}
        className="bg-blue-600 text-white py-4 px-8 rounded-lg font-semibold text-lg
                 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                 flex items-center justify-center space-x-2 rtl:space-x-reverse mx-auto"
      >
        <ExternalLink className="w-5 h-5" />
        <span>עבור לעמוד התשלום</span>
      </motion.button>
    </motion.div>
  );

  /**
   * 🎨 עיצוב שלב 3: המתנה לתשלום
   */
  const renderWaitingStep = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="text-center space-y-6"
    >
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <Loader2 className="w-16 h-16 text-yellow-600 mx-auto mb-4 animate-spin" />
        <h3 className="text-xl font-semibold text-yellow-900 mb-2">
          ממתין לאישור תשלום
        </h3>
        <p className="text-yellow-700">
          אנא השלם את התשלום בחלון שנפתח
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-blue-700 text-sm">
          🔄 בודק סטטוס תשלום כל 3 שניות...
        </p>
      </div>

      <button
        onClick={checkPaymentStatus}
        className="text-blue-600 hover:text-blue-800 underline"
      >
        בדוק סטטוס תשלום ידנית
      </button>
    </motion.div>
  );

  /**
   * 🎨 עיצוב שלב 4: הצלחה
   */
  const renderSuccessStep = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center space-y-6"
    >
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-green-900 mb-2">
          התשלום בוצע בהצלחה! 🎉
        </h3>
        <p className="text-green-700">
          תודה על הרכישה. אישור נשלח לאימייל שלך
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <Receipt className="w-8 h-8 text-blue-600 mx-auto mb-2" />
        <p className="text-blue-700 text-sm">
          חשבונית אוטומטית תישלח תוך מספר דקות
        </p>
      </div>
    </motion.div>
  );

  /**
   * 🎨 רנדור ראשי
   */
  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      {/* אינדיקטור שלבים */}
      <div className="flex items-center justify-between mb-8">
        {[1, 2, 3, 4].map((step) => (
          <div key={step} className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium
              ${currentStep >= step 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-600'
              }`}>
              {step}
            </div>
            {step < 4 && (
              <div className={`h-1 w-16 mx-2 
                ${currentStep > step ? 'bg-blue-600' : 'bg-gray-200'}
              `} />
            )}
          </div>
        ))}
      </div>

      {/* כותרת */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          תשלום דרך כספית
        </h2>
        <p className="text-gray-600">
          סכום לתשלום: ₪{totalAmount?.toFixed(2)}
        </p>
      </div>

      {/* שלבי התשלום */}
      {currentStep === 1 && renderCustomerDetailsStep()}
      {currentStep === 2 && renderPaymentRedirectStep()}
      {currentStep === 3 && renderWaitingStep()}
      {currentStep === 4 && renderSuccessStep()}
    </div>
  );
};

export default HypayPaymentForm; 